/**
 * Recurring invoices (design I) and the detail page's Next reminder card
 * (design D), the pure half: the Make recurring form (default date, summary
 * sentence, validation, the POST body), list row copy, and what the reminder
 * card says. The summary sentence mirrors the server's scheduleSummary
 * (lambda/invoices/recurring.mjs) so the modal promises what the list shows.
 */
import type {
  Channel, InvoiceDoc, InvoiceSettings, Recurring, RecurringInput,
} from '../../internals/repository/InvoiceRepository';
import { date, todayColombo } from './format';

export type Frequency = RecurringInput['frequency'];
export type RecurringMode = RecurringInput['mode'];
export type EndMode = 'NEVER' | 'DATE' | 'COUNT';

export const FREQUENCIES: { key: Frequency; label: string; unit: string }[] = [
  { key: 'WEEKLY', label: 'Weekly', unit: 'week' },
  { key: 'MONTHLY', label: 'Monthly', unit: 'month' },
  { key: 'QUARTERLY', label: 'Quarterly', unit: 'quarter' },
  { key: 'YEARLY', label: 'Yearly', unit: 'year' },
];
export const frequencyLabel = (f?: string) => FREQUENCIES.find((x) => x.key === f)?.label ?? f ?? '—';

export const MODES: { key: RecurringMode; label: string; short: string; desc: string }[] = [
  { key: 'DRAFT', label: 'Create a draft for review', short: 'Create draft', desc: 'You review and issue each invoice.' },
  { key: 'ISSUE_AND_SEND', label: 'Issue and send', short: 'Issue and send', desc: 'Netlise issues it and sends it to the customer.' },
];
export const modeLabel = (m?: string) => MODES.find((x) => x.key === m)?.short ?? '—';

/** Display order for channels in sentences and pills: WhatsApp, SMS, email. */
export const SEND_ORDER: Channel[] = ['WHATSAPP', 'SMS', 'EMAIL'];
export const CHANNEL_WORD: Record<Channel, string> = { WHATSAPP: 'WhatsApp', SMS: 'SMS', EMAIL: 'email' };
export const CHANNEL_PILL: Record<Channel, string> = { WHATSAPP: 'WhatsApp', SMS: 'SMS', EMAIL: 'Email' };
export const sendOrder = (list: readonly string[] = []): Channel[] => SEND_ORDER.filter((c) => list.includes(c));

/** "a", "a and b", "a, b and c" (the server's joinAnd). */
export const joinAnd = (xs: string[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const pad = (n: number) => String(n).padStart(2, '0');
const isoOf = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;
export const isIsoDate = (s?: string) => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(Date.parse(`${s}T00:00:00Z`))
  && new Date(`${s}T00:00:00Z`).toISOString().slice(0, 10) === s;

export function ordinal(n: number): string {
  const t = n % 100;
  if (t >= 11 && t <= 13) return `${n}th`;
  return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] || 'th'}`;
}

/**
 * The first day of the next period after `today`: next Monday, the 1st of
 * next month, the first day of the next calendar quarter, or 1 Jan next year.
 */
export function nextPeriodStart(frequency: Frequency, today = todayColombo()): string {
  const [y, m] = today.split('-').map(Number);
  if (frequency === 'WEEKLY') {
    const d = new Date(`${today}T00:00:00Z`);
    const toMonday = ((8 - d.getUTCDay()) % 7) || 7;
    d.setUTCDate(d.getUTCDate() + toMonday);
    return d.toISOString().slice(0, 10);
  }
  if (frequency === 'QUARTERLY') {
    const q = Math.floor((m - 1) / 3) + 1; // 1..4
    return q === 4 ? isoOf(y + 1, 1, 1) : isoOf(y, q * 3 + 1, 1);
  }
  if (frequency === 'YEARLY') return isoOf(y + 1, 1, 1);
  return m === 12 ? isoOf(y + 1, 1, 1) : isoOf(y, m + 1, 1);
}

/* ── The Make recurring form ─────────────────────────────────────────── */

export interface RecurringForm {
  name: string;
  frequency: Frequency;
  startDate: string;
  endMode: EndMode;
  endDate: string;
  count: string;
  mode: RecurringMode;
  channels: Channel[];
}

/** The source the modal starts from: an InvoiceDoc or a list item. */
export interface RecurringSource {
  docId: string;
  number?: string | null;
  customer?: { name?: string; companyName?: string } | null;
  totalMinor?: number;
}

export const customerName = (c?: { name?: string; companyName?: string } | null) =>
  c?.companyName || c?.name || 'Customer';

export const defaultName = (source: RecurringSource, frequency: Frequency) =>
  `${customerName(source.customer)} · ${frequencyLabel(frequency)}`;

export function initialForm(source: RecurringSource, channels: readonly string[] | undefined, today = todayColombo()): RecurringForm {
  const start = nextPeriodStart('MONTHLY', today);
  return {
    name: defaultName(source, 'MONTHLY'),
    frequency: 'MONTHLY',
    startDate: start,
    endMode: 'NEVER',
    endDate: '',
    count: '12',
    mode: 'ISSUE_AND_SEND',
    channels: sendOrder(channels && channels.length ? channels : ['WHATSAPP', 'EMAIL']),
  };
}

/** "Every month on the 1st, from 1 Nov 2026, issue and send by WhatsApp and email. No end date." */
export function scheduleSummary(f: Pick<RecurringForm, 'frequency' | 'startDate' | 'endMode' | 'endDate' | 'count' | 'mode' | 'channels'>): string {
  const unit = FREQUENCIES.find((x) => x.key === f.frequency)?.unit ?? 'month';
  const every = `Every ${unit}`;
  let on = '';
  let from = '';
  if (isIsoDate(f.startDate)) {
    const [, m, d] = f.startDate.split('-').map(Number);
    if (f.frequency === 'WEEKLY') on = ` on ${WEEKDAYS[new Date(`${f.startDate}T00:00:00Z`).getUTCDay()]}`;
    else if (f.frequency === 'YEARLY') on = ` on ${d} ${MONTHS[m - 1]}`;
    else on = ` on the ${ordinal(d)}${d > 28 ? ' (or the last day of a shorter month)' : ''}`;
    from = `, from ${date(f.startDate)}`;
  }
  let action: string;
  const chs = sendOrder(f.channels);
  if (f.mode === 'DRAFT') action = 'create a draft for you to review';
  else if (chs.length) action = `issue and send by ${joinAnd(chs.map((c) => CHANNEL_WORD[c]))}`;
  else action = 'issue it without sending';
  let ending = 'No end date.';
  const n = Number(f.count);
  if (f.endMode === 'DATE') ending = isIsoDate(f.endDate) ? `Ends on ${date(f.endDate)}.` : 'Ends on a date you choose.';
  if (f.endMode === 'COUNT') ending = Number.isInteger(n) && n >= 1 ? `Ends after ${n} invoice${n === 1 ? '' : 's'}.` : 'Ends after a number of invoices.';
  return `${every}${on}${from}, ${action}. ${ending}`;
}

export const MAX_NAME = 120;
export const MAX_COUNT = 1000;

/** Client-side problems keyed by the server's field paths. */
export function validateRecurring(f: RecurringForm, today = todayColombo()): Record<string, string> {
  const out: Record<string, string> = {};
  const name = f.name.trim();
  if (!name) out.name = 'Enter a name.';
  else if (name.length > MAX_NAME) out.name = `Keep it to ${MAX_NAME} characters.`;
  if (!isIsoDate(f.startDate)) out.startDate = 'Choose the first invoice date.';
  else if (f.startDate < today) out.startDate = 'Choose today or a later date.';
  if (f.endMode === 'DATE') {
    if (!isIsoDate(f.endDate)) out['end.date'] = 'Choose the end date.';
    else if (isIsoDate(f.startDate) && f.endDate <= f.startDate) out['end.date'] = 'Choose a date after the first invoice.';
  }
  if (f.endMode === 'COUNT') {
    const t = f.count.trim();
    if (!/^\d+$/.test(t) || Number(t) < 1) out['end.count'] = 'Enter 1 or more.';
    else if (Number(t) > MAX_COUNT) out['end.count'] = `Enter at most ${MAX_COUNT}.`;
  }
  if (f.mode === 'ISSUE_AND_SEND' && !f.channels.length) out.channels = 'Choose at least one channel.';
  return out;
}

/** The POST /invoices/recurring body. */
export function buildRecurringInput(f: RecurringForm, fromDocId: string): RecurringInput {
  const end: RecurringInput['end'] = f.endMode === 'DATE'
    ? { type: 'DATE', date: f.endDate }
    : f.endMode === 'COUNT' ? { type: 'COUNT', count: Number(f.count.trim()) } : { type: 'NEVER' };
  return {
    fromDocId,
    name: f.name.trim(),
    frequency: f.frequency,
    interval: 1,
    startDate: f.startDate,
    end,
    mode: f.mode,
    channels: f.mode === 'DRAFT' ? [] : sendOrder(f.channels),
  };
}

const FIELD_COPY: Record<string, Record<string, string>> = {
  name: { required: 'Enter a name.', too_long: `Keep it to ${MAX_NAME} characters.` },
  startDate: { in_past: 'Choose today or a later date.', invalid: 'Choose a valid date.', required: 'Choose the first invoice date.' },
  'end.date': { before_start_date: 'Choose a date after the first invoice.', invalid: 'Choose a valid date.' },
  'end.count': { invalid: `Enter 1 to ${MAX_COUNT}.` },
  channels: { invalid: 'Choose email, SMS or WhatsApp.' },
  fromDocId: {
    not_found: 'The invoice was not found. It may have been deleted.',
    void: 'A void invoice cannot be made recurring.',
    no_lines: 'The invoice has no lines to repeat.',
    not_an_invoice: 'Only invoices can be made recurring.',
    required: 'Choose an invoice to start from.',
  },
};

/** Server field code → the copy shown under the field (or in the banner for fromDocId). */
export const recurringFieldMessage = (path: string, code: string) =>
  FIELD_COPY[path]?.[code] ?? 'Check this value.';

export const RECURRING_FORBIDDEN = 'Only the owner or an admin can manage recurring invoices.';
export const isForbidden = (e: any) => e?.status === 403 || e?.code === 'forbidden_role';

/* ── The list ────────────────────────────────────────────────────────── */

export type RecurringStatus = Recurring['status'];
export const STATUS_CHIP: Record<RecurringStatus, { label: string; bg: string; fg: string; dot: string }> = {
  ACTIVE: { label: 'Active', bg: 'var(--posSoft)', fg: 'var(--posFg)', dot: 'var(--pos)' },
  PAUSED: { label: 'Paused', bg: 'var(--neutralSoft)', fg: 'var(--text2)', dot: 'var(--text3)' },
  ENDED: { label: 'Ended', bg: 'var(--neutralSoft)', fg: 'var(--text3)', dot: 'var(--text3)' },
};

/** Amount per run: the stored amount, else the template's total. */
export const recurringAmount = (r: Recurring): number | null => {
  const a = (r as any).amountMinor ?? r.template?.totalMinor;
  return typeof a === 'number' ? a : null;
};

export const recurringCustomer = (r: Recurring) => customerName((r as any).customer ?? (r as any).customerSnapshot ?? r.template?.customer);

/** "3 created · No end date" / "· Until 31 Dec 2027" / "· 3 of 12". */
export function recurringMeta(r: Recurring): string {
  const made = r.createdCount ?? 0;
  const end = (r.end ?? { type: 'NEVER' }) as any;
  let tail = 'No end date';
  if (end.type === 'DATE') tail = `Until ${date(end.date)}`;
  if (end.type === 'COUNT') tail = `${made} of ${end.count}`;
  return end.type === 'COUNT' ? `${tail} created` : `${made} created · ${tail}`;
}

export function nextRunText(r: Recurring): string {
  if (r.status === 'ENDED') return 'Ended';
  if (r.status === 'PAUSED') return 'Paused';
  return r.nextRunDate ? date(r.nextRunDate) : '—';
}

const STATUS_RANK: Record<string, number> = { ACTIVE: 0, PAUSED: 1, ENDED: 2 };
/** Active first (soonest run first), then paused, then ended. */
export const sortRecurring = (items: Recurring[]) => [...items].sort((a, b) =>
  (STATUS_RANK[a.status] ?? 3) - (STATUS_RANK[b.status] ?? 3)
  || String(a.nextRunDate ?? '9999').localeCompare(String(b.nextRunDate ?? '9999'))
  || a.name.localeCompare(b.name));

/* ── Next reminder card (design D) ───────────────────────────────────── */

const COLOMBO = 330 * 60 * 1000;

/** ms → "24 Oct 2026, 08:00" in Asia/Colombo, the time the server sends at. */
export function colomboDateTime(ms: number): string {
  const iso = new Date(ms + COLOMBO).toISOString();
  return `${date(iso.slice(0, 10))}, ${iso.slice(11, 16)}`;
}

export type ReminderCardState = 'scheduled' | 'paused' | 'claim' | 'none';
export interface ReminderCardView {
  state: ReminderCardState;
  title: string;
  when?: string;
  line: string;
  template?: 'reminder' | 'overdue';
  action: 'pause' | 'resume' | null;
}

export function reminderChannels(settings?: InvoiceSettings | null): Channel[] {
  const list = settings?.reminders?.channels ?? settings?.defaultChannels ?? ['WHATSAPP', 'EMAIL'];
  return sendOrder(list as string[]);
}

/** What the Next reminder card says for a document, from its `reminders` and the shop's settings. */
export function nextReminderView(doc: InvoiceDoc, settings?: InvoiceSettings | null): ReminderCardView {
  const r = (doc.reminders ?? { paused: false }) as InvoiceDoc['reminders'] & { sent?: string[] };
  const closed = doc.status === 'DRAFT' || doc.status === 'VOID' || doc.status === 'PAID' || !(doc.balanceMinor > 0);
  if (r.paused) {
    return { state: 'paused', title: 'Reminders paused', line: 'No automatic reminders will be sent.', action: closed ? null : 'resume' };
  }
  if ((doc.pendingClaimMinor || 0) > 0) {
    return { state: 'claim', title: 'Reminders on hold', line: 'Paused while a payment waits for approval.', action: 'pause' };
  }
  const chs = reminderChannels(settings);
  if (r.nextAt && !closed) {
    const slotDate = new Date(r.nextAt + COLOMBO).toISOString().slice(0, 10);
    const overdue = !!doc.dueDate && slotDate > doc.dueDate;
    const what = overdue ? 'Overdue notice' : 'Payment reminder';
    return {
      state: 'scheduled',
      title: 'Next reminder',
      when: colomboDateTime(r.nextAt),
      template: overdue ? 'overdue' : 'reminder',
      line: chs.length ? `${what} by ${joinAnd(chs.map((c) => CHANNEL_WORD[c]))}, within your sending hours.` : `${what}. No reminder channels are set up.`,
      action: 'pause',
    };
  }
  let line = 'All scheduled reminders have been sent.';
  if (doc.status === 'PAID' || (doc.status !== 'DRAFT' && doc.status !== 'VOID' && !(doc.balanceMinor > 0))) line = 'This invoice is paid in full.';
  else if (doc.status === 'VOID') line = 'Reminders stopped when the invoice was voided.';
  else if (doc.status === 'DRAFT') line = 'Reminders start when the invoice is issued.';
  else if (!doc.dueDate) line = 'This invoice has no due date.';
  else if (!r.sent?.length) line = 'Your reminder settings have none left for this invoice.';
  return { state: 'none', title: 'No more reminders scheduled', line, action: closed ? null : 'pause' };
}
